/**
 * Product Description Accordion Component
 * Handles expand/collapse functionality for product descriptions and metafields
 */

class ProductDescriptionAccordion extends HTMLElement {
  constructor() {
    super();
    
    // Property Initializations
    this.selectorInteractiveElements = "button, [href], input, select, [tabindex]";
    this.triggerClass = ".wt-collapse__trigger";
    this.classActiveTrigger = "wt-collapse__trigger--active";
    
    // Element References
    this.trigger = this.querySelector(this.triggerClass);
    this.content = this.querySelector(".wt-collapse__target");
    
    // Event delegation method binding
    this.handleDelegatedEvent = this.handleDelegatedEvent.bind(this);
  }

  static get observedAttributes() {
    return ["data-open"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-open" && oldValue !== newValue) {
      this.handleAriaAndTabindex();
    }
  }

  connectedCallback() {
    this.initialize();
    
    // Add event delegation listener to the document
    document.addEventListener("click", this.handleDelegatedEvent);
  }

  disconnectedCallback() {
    // Remove delegated event listener
    document.removeEventListener("click", this.handleDelegatedEvent);
  }

  isOpen() {
    return this.dataset.open === "true";
  }

  getInteractiveElements(container) {
    return Array.from(
      container.querySelectorAll(this.selectorInteractiveElements)
    ).filter((el) => !el.hasAttribute("disabled"));
  }

  handleAriaAndTabindex() {
    if (this.isOpen()) {
      this.trigger.setAttribute("aria-expanded", "true");
      this.setTabindex(this.getInteractiveElements(this.content), "0");
    } else {
      this.trigger.setAttribute("aria-expanded", "false");
      this.setTabindex(this.getInteractiveElements(this.content), "-1");
    }
  }

  setTabindex(elements, value) {
    elements.forEach((el) => el.setAttribute("tabindex", value));
  }

  toggleState() {
    const isOpen = this.isOpen();
    this.dataset.open = isOpen ? "false" : "true";
    this.trigger.classList.toggle(this.classActiveTrigger);
    
    // Close other accordions in the same container (accordion behavior)
    const container = this.closest('.product-description-accordion');
    if (container && !isOpen) {
      const otherAccordions = container.querySelectorAll('product-description-accordion');
      otherAccordions.forEach(accordion => {
        if (accordion !== this && accordion.isOpen()) {
          accordion.dataset.open = "false";
          const otherTrigger = accordion.querySelector(this.triggerClass);
          if (otherTrigger) {
            otherTrigger.classList.remove(this.classActiveTrigger);
          }
        }
      });
    }
  }

  handleDelegatedEvent(event) {
    const trigger = event.target.closest(this.triggerClass);
    if (!trigger || !this.contains(trigger)) {
      return; // Clicked outside the trigger
    }

    event.preventDefault();
    this.toggleState();
  }

  initialize() {
    // Set initial state
    this.dataset.open = this.dataset.open || "false";
    
    if (this.dataset.open === "true") {
      this.trigger.classList.add(this.classActiveTrigger);
    }
    
    this.trigger.setAttribute("tabindex", "0");
    this.handleAriaAndTabindex();
  }
}

// Define the custom element
customElements.define("product-description-accordion", ProductDescriptionAccordion);

// Initialize accordions when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Handle keyboard navigation
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      const trigger = e.target.closest('.wt-collapse__trigger');
      if (trigger) {
        e.preventDefault();
        trigger.click();
      }
    }
  });
});
